import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  applyFullGlobalTheme,
  type GlobalThemeConfig,
} from '@/hooks/useGlobalTheme';

export interface PortalPublicBranding {
  navBgColor: string;
  navTextColor: string;
  primaryColor: string;
  accentColor: string;
  navActiveColor: string;
  primaryHoverColor: string;
  fontFamily: string;
  logoUrl: string | null;
}

const DEFAULT_PUBLIC_BRANDING: PortalPublicBranding = {
  navBgColor: '#0F3A3F',
  navTextColor: '#FFFFFF',
  primaryColor: '#3B82F6',
  accentColor: '#1F2937',
  navActiveColor: '#0F3A3F',
  primaryHoverColor: '#00917E',
  fontFamily: 'inter',
  logoUrl: null,
};

function toGlobalThemeConfig(branding: PortalPublicBranding): GlobalThemeConfig {
  return {
    id: 1,
    primaryColor: branding.primaryColor,
    accentColor: branding.accentColor,
    navBgColor: branding.navBgColor,
    navTextColor: branding.navTextColor,
    navActiveColor: branding.navActiveColor,
    primaryHoverColor: branding.primaryHoverColor,
    fontFamily: branding.fontFamily,
    logoUrl: branding.logoUrl,
    updatedAt: new Date().toISOString(),
  };
}

async function fetchPortalPublicBranding(): Promise<PortalPublicBranding> {
  const response = await fetch('/api/look-and-feel/public');
  if (!response.ok) {
    return DEFAULT_PUBLIC_BRANDING;
  }
  const data = await response.json() as Partial<PortalPublicBranding>;
  return {
    ...DEFAULT_PUBLIC_BRANDING,
    ...data,
  };
}

/**
 * Loads Look & Feel branding for portal-external users via the public endpoint.
 * Admin users should continue to use useGlobalTheme() instead.
 */
export function usePortalBranding() {
  const query = useQuery<PortalPublicBranding>({
    queryKey: ['portal-public-branding'],
    queryFn: fetchPortalPublicBranding,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: true,
  });

  useEffect(() => {
    if (query.data) {
      applyFullGlobalTheme(toGlobalThemeConfig(query.data));
    }
  }, [query.data]);

  return {
    branding: query.data ?? DEFAULT_PUBLIC_BRANDING,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
